import React from 'react';

/**
 * THE header band — the row of page chrome that sits ABOVE a `ui/Toolbar` and
 * SCROLLS AWAY with the page.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE TWO-BAND MODEL — "context above, controls stick"
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Product owner, 2026-08-08, of Department Orders: *"all of this is not aligned,
 * on the same rows etc. It's a mess"*, then *"I would rather a professional grade
 * UI layout… It should be as unified across the site as possible"*. He was shown
 * three options and chose this one.
 *
 *   ┌─ HEADER BAND (this component) — SCROLLS AWAY ────────────────────────────┐
 *   │  All orders · 120 shown · 52 actionable              £ Ex VAT  ⟳ ⤓ [+New]│
 *   ├─ STICKY BAND (`ui/Toolbar`) — PINS ──────────────────────────────────────┤
 *   │  [🔍 Search…]  [All │ Actionable]  [Sort ▾] [Filters ▾]   [Cards │ Table]│
 *   └──────────────────────────────────────────────────────────────────────────┘
 *
 * 🔑 **THE RULE THAT DECIDES EVERY CASE THIS COMPONENT DOES NOT ANTICIPATE:**
 * the sticky band holds only what you reach for **while scrolling**. You search
 * and filter mid-scroll; you read a count once and you refresh at the start or
 * the end of a task. **If a control is not used mid-scroll, it belongs here.**
 * That is why `Refresh` and `Export` moved up out of the Breakdown's toolbar and
 * why `Sort ▾` did not.
 *
 * ── EACH BAND HAS A VISUAL JOB, AND THE CONTRAST IS THE POINT ──
 * Header: **muted, no borders, no fills** — text you READ.
 * Sticky: **bordered/ghost controls** — things you CLICK.
 * Without that contrast the two bands read as one bar that accidentally wrapped.
 * 🚨 **ONE `border-b` UNDER THE WHOLE THING, NEVER BETWEEN THE BANDS.** The
 * `border-b` lives on `TOOLBAR_SHELL`, under the sticky band, so the two bands
 * are visibly one unit of page chrome. Adding a rule between them splits it into
 * two competing headers, which is the thing being fixed.
 *
 * ── ⚠️ IT MUST NOT BE STICKY, AND `ui/Toolbar`'s OFFSET IS UNCHANGED ──
 * `top-0 lg:top-14` on the toolbar is STATIC on purpose and matches
 * `APP_BAR_LG_PX = 56` (`hooks/useStickyToolbar.js`). Because this band scrolls
 * away rather than pinning, **the toolbar still pins under the same thing it
 * pinned under before** — the Layout app bar — and that offset needed no
 * retuning. If you ever make this band sticky, the offset becomes
 * `56 + this band's measured height` and must be measured, not guessed: read
 * `useStickyToolbar`'s `headerTop`, which already does exactly that arithmetic
 * for second-level sticky headers.
 *
 * ── SLOTS, NOT KNOWLEDGE ──
 * This component knows nothing about orders, gags, counts or permissions. It
 * takes finished nodes and lays them out. Four pages must not each hand-roll a
 * header row (CLAUDE.md → Code Unification Principle), and a shared component
 * that grew a `count` prop or an `isAdmin` prop would be four pages' logic
 * wearing one component's name.
 *
 * Props:
 *  - `context`  — LEFT. The muted sentence: page title · N shown · the one
 *                 contextual number that matters. A node, so a page can colour
 *                 its own emphasis span.
 *  - `status`   — RIGHT, first. Status badges (the VAT mode badge). Separated
 *                 from `actions` by a hairline, because status is not an action.
 *  - `actions`  — RIGHT, last. `Refresh`, `Export`, and any primary create.
 *  - `testId`   — optional `data-testid` on the band.
 *
 * 🟢 Every slot is optional and an omitted slot renders NOTHING — not an empty
 * flex child, which would still take the gap and leave the hairline floating.
 * `Approvals` passes no `actions`; the Breakdown passes no `context` (its count
 * lives in the Layout app bar via `usePageSummary` — see CLAUDE.md).
 */
export default function PageHeaderBand({ context, status, actions, testId, hideContextOnPhone = false }) {
  const hasStatus = Boolean(status);
  const hasActions = Boolean(actions);

  // Nothing to draw at all. Returning `null` rather than an empty flex box is
  // still the right call now the bottom margin is gone: an empty band would
  // otherwise sit in the DOM as a zero-height element that any future spacing
  // utility silently turns back into a gap above the toolbar.
  if (!context && !hasStatus && !hasActions) return null;

  // 📱 `hideContextOnPhone` (2026-10-03, the order/hire pages): below `md` the
  // context line ("All orders · N shown") is hidden — the tab badges carry the
  // counts — and with nothing else in it the whole band goes. Desktop unchanged.
  const phoneHide = hideContextOnPhone ? ' max-md:hidden' : '';
  return (
    <div
      // 🔻 NO BOTTOM MARGIN, by product-owner ruling 2026-08-08 ("bottom margin
      // … can be reduced to 0 i think"). The header band sits directly on the
      // sticky toolbar; the toolbar's own `py` is the only breathing room the
      // pair gets, and that is deliberate — the two bands read as ONE piece of
      // page chrome, which is the whole point of the `border-b` living under
      // both rather than between them. ⚠️ If a gap is ever wanted back, it is
      // the TOOLBAR's `marginClassName` prop that owns vertical spacing on this
      // pair — do not re-add an `mb-*` here and end up with two owners.
      className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2${hasStatus || hasActions ? '' : phoneHide}`}
      data-testid={testId}
    >
      {/* LEFT — muted context. `min-w-0` so a long sentence truncates inside its
          own box instead of shoving the action cluster off the row: a flex
          item's default `min-width: auto` pins it at its content width, the same
          trap `constants/inputs.js` documents for the search box. */}
      <div className={`min-w-0 text-sm text-gray-500 dark:text-gray-400${phoneHide}`}>
        {context}
      </div>

      {/* RIGHT — status, hairline, actions. `ml-auto` rather than relying on
          `justify-between` alone, so the cluster stays pinned right even when
          `context` is absent (the Breakdown). Both bands right-align their
          actions, which is what puts a vertical edge down the right of the page
          chrome. */}
      {(hasStatus || hasActions) && (
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {status}
          {/* The hairline. Rendered only when it has something on BOTH sides —
              a divider with nothing after it is a stray mark. `hidden sm:block`
              because once the cluster wraps onto its own line the divider is
              separating things that are no longer side by side. */}
          {hasStatus && hasActions && (
            <span
              aria-hidden="true"
              className="hidden sm:block h-5 w-px bg-gray-300 dark:bg-gray-600"
            />
          )}
          {actions}
        </div>
      )}
    </div>
  );
}
